import type { CSSProperties } from 'react';

const SUPPORT_EMAIL = 'application.security@kone.com';

// KONE palette. The CSS variables come from the app's theme.css; the fallbacks
// keep the page correct if it is ever rendered without that stylesheet.
const BLUE = 'var(--kone-blue, #1450F5)';
const TEXT = 'var(--kone-text, #141414)';
const MUTED = 'var(--kone-text-muted, rgba(20, 20, 20, 0.72))';
const BORDER = 'var(--kone-border, #E7EBF0)';
const SURFACE_ALT = 'var(--kone-surface-alt, #F8FAFC)';

const styles: Record<string, CSSProperties> = {
  page: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 48,
    padding: '64px 48px',
    minHeight: '70vh',
    boxSizing: 'border-box',
    color: TEXT,
  },
  text: { flex: '1 1 420px', maxWidth: 560 },
  code: {
    margin: 0,
    fontSize: 96,
    lineHeight: 1,
    fontWeight: 800,
    color: BLUE,
  },
  heading: { margin: '8px 0 12px', fontSize: 30, fontWeight: 700 },
  lead: { margin: '0 0 24px', fontSize: 16, color: MUTED },
  card: {
    margin: '0 0 24px',
    padding: '20px 24px',
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    background: SURFACE_ALT,
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    fontSize: 16,
  },
  step: { display: 'flex', alignItems: 'flex-start', gap: 14 },
  badge: {
    flex: '0 0 28px',
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: BLUE,
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: 700,
    lineHeight: '28px',
    textAlign: 'center',
  },
  actions: { display: 'flex', flexWrap: 'wrap', gap: 16 },
  primary: {
    padding: '12px 24px',
    borderRadius: 8,
    background: BLUE,
    border: `2px solid ${BLUE}`,
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 600,
    textDecoration: 'none',
  },
  footnote: { margin: '24px 0 0', fontSize: 12, color: MUTED },
  art: { flex: '0 1 340px', width: '100%', maxWidth: 340, height: 'auto' },
};

/** Decorative shield-and-lock illustration, drawn with the KONE palette. */
function ShieldIllustration() {
  return (
    <svg
      viewBox="0 0 340 420"
      style={styles.art}
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M120 30C200-10 320 20 335 120 350 220 300 290 240 350 180 410 90 430 40 370-10 310 30 70 120 30Z"
        fill="#D2F5FF"
      />
      <circle cx="60" cy="60" r="22" fill="#FFE141" />
      <circle cx="318" cy="60" r="12" fill="#FFCDD7" />
      <circle cx="290" cy="380" r="48" fill="#F3EEE6" />
      <g transform="translate(70 70)">
        <path
          d="M100 0 200 36V130C200 200 156 252 100 280 44 252 0 200 0 130V36Z"
          fill="#1450F5"
        />
        <path
          d="M100 18 184 48V130C184 190 148 234 100 258 52 234 16 190 16 130V48Z"
          fill="#1A5BFF"
          opacity="0.6"
        />
        <rect x="62" y="116" width="76" height="62" rx="10" fill="#FFFFFF" />
        <path
          d="M78 116V98a22 22 0 0 1 44 0V116"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="12"
          strokeLinecap="round"
        />
        <circle cx="100" cy="142" r="8" fill="#141414" />
        <rect x="96" y="144" width="8" height="20" rx="3" fill="#141414" />
        <circle cx="186" cy="262" r="26" fill="#AAE1C8" />
        <path
          d="m174 262 9 9 17-19"
          fill="none"
          stroke="#141414"
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

/**
 * The page shown when a user opens a KSDP page their roles do not grant.
 *
 * Deliberately not Backstage's `ErrorPage`: that component always renders a
 * "contact support" link that defaults to the public Backstage GitHub issues
 * page, which KSDP users must not be sent to. This page links only to the
 * AppSec mailbox.
 */
export function KsdpAccessDenied() {
  return (
    <div role="alert" style={styles.page}>
      <div style={styles.text}>
        <p style={styles.code}>403</p>
        <h2 style={styles.heading}>You do not have access to this page.</h2>
        <p style={styles.lead}>
          Your account is signed in, but it does not hold a role that grants
          this page.
        </p>
        <ol style={styles.card}>
          <li style={styles.step}>
            <span style={styles.badge} aria-hidden="true">
              1
            </span>
            <span>
              Go to MyAccess and request{' '}
              <strong>KONE Secure Default Platform: Default User</strong>
            </span>
          </li>
          <li style={styles.step}>
            <span style={styles.badge} aria-hidden="true">
              2
            </span>
            <span>
              Need special access? Reach out to{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                style={{ color: BLUE, fontWeight: 700 }}
              >
                {SUPPORT_EMAIL}
              </a>
            </span>
          </li>
        </ol>
        <div style={styles.actions}>
          <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.primary}>
            Email Application Security
          </a>
        </div>
        <p style={styles.footnote}>
          ERROR 403 · Access is granted through KSDP roles in MyAccess. Sign in
          again after the role is approved.
        </p>
      </div>
      <ShieldIllustration />
    </div>
  );
}
